import React, { useEffect, useState } from "react";
import classnames from "classnames";
import styles from "@/components/Video/Player/video-player.module.scss";
import dynamic from "next/dynamic";
import TextBlock from "@/components/TextBlock/text-block";
import IconPlay from "@ef-global/web-ui-react/lib/icons/IconPlay";
import { VideoPlayerStoryblok } from "@/types/component-types-sb";
const SbEditable = dynamic(() => import("storyblok-react"));
const ReactPlayer = dynamic(() => import("react-player/lazy"));

interface Props {
  blok: VideoPlayerStoryblok;
  className?: string;
}

type Video = NonNullable<VideoPlayerStoryblok["videos"]>[number];

type VideoWithThumb = Video & {
  thumb: any;
};

const VideoPlayer = (props: Props) => {
  const {
    blok: { videos },
    className,
  } = props;
  const [videosWithThumbs, setVideosWithThumbs] = useState<VideoWithThumb[]>(
    []
  );
  const [currentVideo, setCurrentVideo] = useState<
    VideoWithThumb | Partial<VideoWithThumb>
  >({});
  // todo: better fallback
  const fallbackThumbUrl =
    "https://i.vimeocdn.com/video/1181536735-0df8660ae319ecb8871933b9837a26462f0c4dc27994d35ca32e8afa9ec9f0e6-d_640";
  const updateVideo = (i: number) => {
    setCurrentVideo({ ...videosWithThumbs[i] });
  };

  const getThumb = async (url: string) => {
    const res = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`,
      { method: "GET" }
    );
    return res.json();
  };

  useEffect(() => {
    const withThumbs = async () => {
      const result = (videos || []).map(async (video) => {
        const thumb = await getThumb(String(video.url));
        return { ...video, thumb };
      });
      setVideosWithThumbs(
        await Promise.allSettled(result).then((result) =>
          result
            .filter((r) => r.status === "fulfilled")
            .map((r) => {
              if (r.status !== "fulfilled") {
                throw new Error("Failed to fetch video thumbnail");
              }
              return r.value;
            })
        )
      );
    };
    withThumbs().catch(console.error);
  }, [videos]);

  useEffect(() => {
    videosWithThumbs.length && setCurrentVideo(videosWithThumbs[0]);
  }, [videosWithThumbs]);

  return (
    // @ts-expect-error
    <SbEditable content={props.blok}>
      <div className={classnames(styles.videoPlayer, className)}>
        <div className={styles.videoPlayerMain}>
          <div className={classnames("u-mb-l", styles.reactPlayerWrapper)}>
            {currentVideo ? (
              <ReactPlayer
                url={currentVideo.url}
                className={classnames(styles.reactPlayer)}
                width="100%"
                height="100%"
                controls={true}
                playing={true}
              />
            ) : null}
          </div>
          <div className={styles.videoDescription}>
            <h5>{currentVideo.title}</h5>
            <TextBlock
              content={currentVideo.description}
              _uid=""
              component="text-block"
            />
          </div>
        </div>
        <div
          className={classnames(
            styles.videoPlayerNav,
            (videos || []).length > 9 ? styles.overflowNav : ""
          )}
        >
          {videosWithThumbs?.length
            ? videosWithThumbs.map((video, i) => (
                <div key={video._uid}>
                  <div
                    className={classnames(
                      styles.videoContent,
                      currentVideo._uid === video._uid ? styles.isActive : ""
                    )}
                  >
                    <button
                      onClick={() => updateVideo(i)}
                      className={styles.thumbButton}
                    >
                      <img
                        alt={video.title}
                        src={video?.thumb?.thumbnail_url || fallbackThumbUrl}
                        className={styles.thumb}
                      />

                      <div className={styles.videoOverlay}>
                        <PlayButton />
                      </div>
                    </button>
                    <div className={styles.textContent}>
                      <h6 className="u-mb-0">{video.title}</h6>
                      <TextBlock
                        content={video.short_description}
                        text_style={"paragraph-light-small"}
                        _uid=""
                        component="text-block"
                      />
                    </div>
                  </div>
                </div>
              ))
            : null}
        </div>
      </div>
    </SbEditable>
  );
};

const PlayButton = () => (
  <div className={styles.videoPlay}>
    <IconPlay />
  </div>
);

export default VideoPlayer;
